import { HttpClient } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ColDef, GridReadyEvent } from '@ag-grid-community/core';
import { firstValueFrom } from 'rxjs';

import { AgFns, BaseService, GridOptionsExt, ISortModel, SaveAndQueryComponent } from '@core';
import { environment } from '@env';
import { Program, ProgramReview, ProgramReviewFinding, ReviewResolutionEnum } from '@models';
import { UtilFns } from '@utils';

import { AccountDbQueryService } from '../../services/account-db-query.service';
import { AccountDbSaveService } from '../../services/account-db-save.service';

/** Mirrors ProgramCheckRunDto in ProgramCheckService.cs. PascalCase on the wire. */
interface ProgramCheckRunDto {
  ProgramReviewId: string;
  Summary: string;
  FindingCount: number;
  OpenCount: number;
}

const RESOLUTION_NAMES: Record<number, string> = {
  [ReviewResolutionEnum.Open]: 'Open',
  [ReviewResolutionEnum.Fixed]: 'Fixed',
  [ReviewResolutionEnum.Accepted]: 'Accepted',
  [ReviewResolutionEnum.Dismissed]: 'Dismissed',
};

/**
 * AI Program Check of one program - from AI Program Check in the program menu. GlobalAdmins only.
 *
 * Run AI Program Check has the AI apply the rules in effect (master, supplier, account and program
 * overrides) to each configuration of the program, and saves the findings. This page shows the latest
 * check. A person resolves each finding: Accepted (intended) or Dismissed (the AI was wrong), each with
 * a reason. Fixed comes only from a new check, after the configuration is changed. The next check keeps
 * Accepted and Dismissed findings that come back unchanged.
 */
@Component({
  selector: 'prox-program-review',
  templateUrl: './program-review.component.html',
  standalone: false,
})
export class ProgramReviewComponent extends SaveAndQueryComponent {
  private http = inject(HttpClient);

  programId!: string;
  program!: Program;
  review?: ProgramReview;
  findings: ProgramReviewFinding[] = [];
  gridOptions: GridOptionsExt = {};

  isRunning = false;
  runElapsed = 0;
  runError?: string;

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbSaveService: AccountDbSaveService,
    override dbQueryService: AccountDbQueryService,
  ) {
    super(baseService, route, dbSaveService, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.programId = params['programId'];
    UtilFns.assertNonEmptyString(this.programId, 'programId');
    this.program = await this.dbQueryService.getProgramByIdSimple(this.programId);
    UtilFns.assertNonNull(this.program, 'Program');

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      getRowId: (params) => params.data.id,
    });
    await this.loadReview();

    this.setTitle('AI Program Check: ' + this.program.name);
    this.isPageReady = true;
  }

  private async loadReview() {
    this.review = await this.dbQueryService.getLatestProgramReview(this.programId);
    this.findings = [...(this.review?.programReviewFindings ?? [])];
  }

  onGridReady(event: GridReadyEvent) {
    // Long text wraps. The theme sets a cell's line-height to the row height, so the text sits in its
    // own element with a normal line-height; textContent, never HTML, because the text is AI output.
    const wrapped = {
      wrapText: true,
      autoHeight: true,
      cellRenderer: (p: { value?: string }) => {
        const el = document.createElement('div');
        el.className = 'whitespace-normal leading-snug py-2';
        el.textContent = p.value ?? '';
        return el;
      },
    };
    const editable = (f?: ProgramReviewFinding) => !!f && !this.isReadOnly() && f.reviewResolutionId !== ReviewResolutionEnum.Fixed;
    const colDefs: ColDef<ProgramReviewFinding>[] = [
      {
        headerName: 'Configuration',
        colId: 'config',
        valueGetter: (p) => p.data?.productTypeConfig?.name ?? '',
        filter: 'agTextColumnFilter',
        ...wrapped,
        ...AgFns.flexWidth(130),
      },
      {
        headerName: 'Manufacturer / Product Type',
        colId: 'style',
        valueGetter: (p) => {
          const pt = p.data?.productType;
          return pt ? [pt.manufacturer?.name, pt.productKey, pt.name].filter((x) => !!x).join(' / ') : '';
        },
        filter: 'agTextColumnFilter',
        ...wrapped,
        ...AgFns.flexWidth(220),
      },
      {
        headerName: 'Rule',
        colId: 'rule',
        valueGetter: (p) => (p.data ? (p.data.guideline?.title ?? (p.data.guidelineId ? 'Rule' : 'Code check')) : ''),
        filter: 'agSetColumnFilter',
        ...wrapped,
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'Severity',
        colId: 'severity',
        valueGetter: (p) => (p.data ? (p.data.isBlocking ? 'Block' : 'Warn') : ''),
        headerTooltip: 'Block: in Enforce mode, an open finding stops activation. Warn: it never does; a reason is optional.',
        filter: 'agSetColumnFilter',
        ...AgFns.fixWidth(90),
      },
      { headerName: 'Finding', field: 'message', ...wrapped, ...AgFns.flexWidth(400) },
      {
        headerName: 'Resolution',
        colId: 'resolution',
        editable: (p) => editable(p.data),
        valueGetter: (p) => (p.data ? RESOLUTION_NAMES[p.data.reviewResolutionId] : ''),
        valueSetter: (p) => {
          const id = Number(Object.keys(RESOLUTION_NAMES).find((k) => RESOLUTION_NAMES[Number(k)] === p.newValue));
          if (!p.data || !id || id === ReviewResolutionEnum.Fixed) return false;
          p.data.reviewResolutionId = id as ReviewResolutionEnum;
          return true;
        },
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: { values: ['Open', 'Accepted', 'Dismissed'] },
        cellClassRules: {
          'red_pointer': (p) => p.data?.reviewResolutionId === ReviewResolutionEnum.Open && !!p.data?.isBlocking,
          'green_pointer': (p) => p.data?.reviewResolutionId === ReviewResolutionEnum.Fixed,
        },
        headerTooltip: 'Accepted: it is intended. Dismissed: the AI was wrong. Both need a reason. Fixed comes from a new check.',
        filter: 'agSetColumnFilter',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Reason',
        field: 'resolutionReason',
        editable: (p) => editable(p.data),
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
        cellEditorParams: { maxLength: 2000, rows: 6, cols: 60 },
        ...wrapped,
        ...AgFns.flexWidth(220),
      },
      {
        headerName: 'Resolved By',
        colId: 'resolvedBy',
        valueGetter: (p) => p.data?.resolvedByUser?.fullName ?? '',
        tooltipValueGetter: (p) => (p.data?.resolvedTs ? new Date(p.data.resolvedTs).toLocaleString() : ''),
        ...AgFns.flexWidth(120),
      },
    ];
    const sortModel: ISortModel = [
      { colId: 'config', sort: 'asc' },
      { colId: 'rule', sort: 'asc' },
    ];
    AgFns.initGrid(event, colDefs, sortModel);
  }

  /** Runs the check on the SAVED program and findings, then shows the new check. */
  async onRunCheck() {
    if (this.hasChanges()) {
      await this.dialogService.okDialog('Save first', 'Save or undo your changes to the findings, then run the AI Program Check.');
      return;
    }
    this.runError = undefined;
    this.isRunning = true;
    this.runElapsed = 0;
    const timer = setInterval(() => this.runElapsed++, 1000);
    try {
      const result = await firstValueFrom(
        this.http.post<ProgramCheckRunDto>(`${environment.apiUrl}ProgramReview/RunProgramCheck?programId=${this.programId}`, {}),
      );
      await this.loadReview();
      AgFns.refreshGrid(this.gridOptions, this.findings);
      this.toastr.success(result.Summary, 'AI Program Check');
    } catch (err: any) {
      this.runError = err?.error?.Message ?? err?.message ?? String(err);
    } finally {
      clearInterval(timer);
      this.isRunning = false;
    }
  }

  /** Open BLOCKING findings: these stop activation in Enforce mode. */
  openCount() {
    return this.findings.filter((f) => f.reviewResolutionId === ReviewResolutionEnum.Open && f.isBlocking).length;
  }

  /** Open warnings: shown, never a reason to refuse activation. */
  warningCount() {
    return this.findings.filter((f) => f.reviewResolutionId === ReviewResolutionEnum.Open && !f.isBlocking).length;
  }

  runText() {
    if (!this.review) return 'This program has no AI Program Check yet.';
    const by = this.review.runByUser?.fullName;
    return `Last check ${new Date(this.review.runTs).toLocaleString()}${by ? ' by ' + by : ''}. ${this.review.summary ?? ''}`;
  }

  override async beforeSave() {
    const problems = this.findings
      .filter((f) => !f.entityAspect.entityState.isUnchanged())
      // A warning may be resolved without a reason; a blocking finding may not.
      .filter((f) => f.isBlocking && f.reviewResolutionId !== ReviewResolutionEnum.Open && !(f.resolutionReason ?? '').trim())
      .map((f) => `${f.productTypeConfig?.name ?? ''}: ${RESOLUTION_NAMES[f.reviewResolutionId]} needs a reason.`);
    if (problems.length > 0) {
      await this.dialogService.okDialog('Cannot save', [...new Set(problems)].join('<br/>'));
      return false;
    }
    return true;
  }

  /** The server stamps who resolved each finding and when, so read them back. */
  override async afterSave() {
    await this.loadReview();
    AgFns.refreshGrid(this.gridOptions, this.findings);
  }

  override async afterUndo() {
    this.gridOptions.apiX?.refreshCells({ force: true });
  }

  isReadOnly() {
    return !this.authUser?.isGlobalAdmin;
  }
}
